import {useEffect, useRef, useState} from 'react';
import {useAffiliatesQuery} from '../api/generated';
import {useAccessLevel} from './useAccessLevel';

const MAX_AFFILIATES_RESULTS = 25;

/**
 * This function is used to get the referral code from the referral link
 * @param referralLink - The referral link
 * @returns The referral code
 */
const getReferralCode = (referralLink: string) => {
  try {
    const url = new URL(referralLink);
    return url.searchParams.get('ref');
  } catch (error) {
    console.error('Invalid URL format:', error);
    return null;
  }
};

/**
 * This hook is used to fetch affiliates and load more affiliates when the user scrolls to the bottom of the list
 */
export const useAffiliates = () => {
  const {isAdmin} = useAccessLevel();
  const [selectOptions, setSelectOptions] = useState<
    Array<{label: string; value: string; id: string}>
  >([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const processedIds = useRef<Set<string>>(new Set());

  const {data: affiliatesData, fetchMore} = useAffiliatesQuery({
    skip: !isAdmin,
    variables: {
      input: {nextToken: page.toString(), affiliateGroupId: 'ag_CDPoDVSEX8rQhQO-X4'}
    }
  });

  useEffect(() => {
    if (affiliatesData?.affiliates?.items?.length === MAX_AFFILIATES_RESULTS) {
      setPage((prev) => prev + 1);
      fetchMore({
        variables: {
          input: {nextToken: page.toString(), affiliateGroupId: 'ag_CDPoDVSEX8rQhQO-X4'}
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            affiliates: {
              items: [...prev.affiliates.items, ...fetchMoreResult.affiliates.items],
              links: fetchMoreResult.affiliates.links
            }
          };
        }
      });
    } else if (affiliatesData && affiliatesData?.affiliates?.items?.length > 0) {
      // Only process new items that haven't been processed before
      const newOptions: Array<{label: string; value: string; id: string}> = [];

      affiliatesData?.affiliates.items?.forEach((item) => {
        // Skip if already processed
        if (processedIds.current.has(item.id)) {
          return;
        }

        if (item.company?.name) {
          const referralCode = item.referral_link?.link
            ? getReferralCode(item.referral_link.link)
            : null;
          if (!referralCode) {
            console.warn(`Missing or invalid referral link for affiliate ${item.id}`);
            return;
          }

          newOptions.push({
            label: item.company.name,
            value: referralCode as string,
            id: item.id
          });

          // Mark this item as processed
          processedIds.current.add(item.id);
        }
      });

      // Only update state if there are new options
      if (newOptions.length > 0) {
        setSelectOptions((prevOptions) => {
          const combinedOptions = [...prevOptions, ...newOptions];
          return combinedOptions.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
        });
      }

      setLoading(false);
    }
  }, [affiliatesData]);

  return {selectOptions, loading};
};
